/** Temporary local-only capture for the next requested development tour. */
class TourPerformance {
  private active = false;
  private started = 0;
  private lastFrame = 0;
  private frames: number[] = [];
  private vectors: number[] = [];
  private longTasks = 0;
  private longTaskMs = 0;
  private tileRequests = 0;
  private tileMs = 0;
  private observers: PerformanceObserver[] = [];
  private timer?: ReturnType<typeof setInterval>;

  start(): void {
    if (this.active || !["localhost", "127.0.0.1"].includes(location.hostname)) return;
    const key = "efb-tour-performance-20260918-second";
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "recording");
    } catch {
      return;
    }
    this.active = true;
    this.started = performance.now();
    this.observe("longtask", (entries) => {
      for (const entry of entries) {
        this.longTasks++;
        this.longTaskMs += entry.duration;
      }
    });
    this.observe("resource", (entries) => {
      for (const entry of entries) {
        if (/tile|pmtiles|\.pbf|\.png|\.jpg/i.test(entry.name)) {
          this.tileRequests++;
          this.tileMs += entry.duration;
        }
      }
    });
    this.timer = setInterval(() => this.publish(false), 5000);
    this.publish(false);
  }

  private observe(type: string, add: (entries: PerformanceEntry[]) => void): void {
    if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return;
    const observer = new PerformanceObserver((list) => add(list.getEntries()));
    observer.observe({ type, buffered: false });
    this.observers.push(observer);
  }

  frame(now: number): void {
    if (!this.active) return;
    if (this.lastFrame && document.visibilityState === "visible") this.frames.push(now - this.lastFrame);
    this.lastFrame = document.visibilityState === "visible" ? now : 0;
    // Bound capture overhead for long destination orbits (about 30 min at 60Hz).
    if (this.frames.length >= 108000) this.stop();
  }

  vector(ms: number): void {
    if (this.active) this.vectors.push(ms);
  }

  private publish(complete: boolean): void {
    const summary = (values: number[]) => {
      const sorted = [...values].sort((a, b) => a - b);
      return {
        count: values.length,
        meanMs: values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0,
        p95Ms: sorted[Math.floor(sorted.length * 0.95)] ?? 0,
        maxMs: sorted[sorted.length - 1] ?? 0,
      };
    };
    const frames = summary(this.frames);
    const report = {
      complete,
      capturedAt: new Date().toISOString(),
      durationSeconds: (performance.now() - this.started) / 1000,
      frames,
      averageFps: frames.meanMs ? 1000 / frames.meanMs : 0,
      framesOver50Ms: this.frames.filter((ms) => ms > 50).length,
      vectorRefresh: summary(this.vectors),
      longTasks: this.longTasks,
      longTaskMs: this.longTaskMs,
      tileRequests: this.tileRequests,
      tileRequestTotalMs: this.tileMs,
      longTasksSupported: PerformanceObserver.supportedEntryTypes?.includes("longtask") ?? false,
    };
    // Only aggregate timings; no coordinates, route names, URLs or user data.
    void fetch("http://127.0.0.1:9197/tour-performance", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(report),
      keepalive: complete,
    }).catch(() => undefined);
  }

  stop(): void {
    if (!this.active) return;
    clearInterval(this.timer);
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];
    this.publish(true);
    this.active = false;
  }
}

export const tourPerformance = new TourPerformance();
